import { INfse } from '../hooks/useNfse';

/**
 * Regras de leitura dos campos fiscais da NFS-e nacional (NT-008 / leiaute DPS),
 * compartilhadas pela tabela do Portal Nacional, pelo Exportar CSV e pelo
 * Exportar PDF da tabela — uma regra só, três superfícies.
 */

/** ISSQN apurado da nota (com fallback pros campos legados, como o DANFSe). */
export const getIssApurado = (n: INfse): number => n.valor_issqn || n.iss || n.issqn || 0;

/**
 * ISS retido na fonte? Leiaute tpRetISSQN: 1 = Não Retido, 2 = Retido pelo
 * Tomador, 3 = Retido pelo Intermediário. (Na base atual só existem 1 e 2;
 * o 3 fica coberto por segurança.)
 */
export const isIssRetido = (n: INfse): boolean => n.tp_ret_issqn === '2' || n.tp_ret_issqn === '3';

/** Valor exibido em "ISS Normal" — exclusivo: retido sai só em "ISS Ret". */
export const getIssProprio = (n: INfse): number => (isIssRetido(n) ? 0 : getIssApurado(n));

/** Valor exibido em "ISS Ret" — exclusivo com "ISS Normal". */
export const getIssRetido = (n: INfse): number => (isIssRetido(n) ? getIssApurado(n) : 0);

/**
 * Indicador de retenção PIS/COFINS (tpRetPisCofins), tabela oficial da
 * NT-007/2026: 0 = PIS/COFINS/CSLL não retidos; 1 = PIS/COFINS retido;
 * 2 = PIS/COFINS não retido; 3 = PIS/COFINS/CSLL retidos; 4 = PIS/COFINS
 * retidos, CSLL não; 5 = só PIS retido; 6 = só COFINS retido; 7 = COFINS/CSLL
 * retidos, PIS não; 8 = só CSLL retida; 9 = PIS/CSLL retidos, COFINS não.
 * A coluna é de PIS/COFINS — o que for só de CSLL (cód. 8) conta como não
 * retido aqui (o valor retido de CSLL aparece na coluna CSLL, via vRetCSLL).
 * Sem código (92% da base) = '-'. NÃO inferir retenção pela presença de
 * vPis/vCofins: a NT-007 é explícita que esses campos são débito de apuração
 * própria e nunca carregam valores retidos.
 */
export const getRetPisCofinsLabel = (tp: string | undefined | null): string => {
  if (tp === '0' || tp === '2' || tp === '8') return 'NÃO RETIDO';
  if (tp === '1' || tp === '3' || tp === '4') return 'RETIDO';
  if (tp === '5' || tp === '9') return 'PIS RETIDO';
  if (tp === '6' || tp === '7') return 'COFINS RETIDO';
  if (tp) return `CÓD. ${tp}`;
  return '-';
};

/**
 * CPF/CNPJ com máscara e padding de zeros à esquerda — mesmas regras do
 * formatCPF/formatCNPJ de FormatFunctions, replicadas aqui porque este módulo
 * precisa ficar livre de dependências de UI (FormatFunctions.tsx importa
 * Chakra/ícones e não pode entrar no grafo dos exporters/testes puros).
 */
export const formatDocumento = (doc: string | number | null | undefined): string => {
  if (!doc) return '-';
  const digitos = String(doc).replace(/\D/g, '');
  if (digitos.length > 11) {
    return digitos
      .padStart(14, '0')
      .replace(/^(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})$/, '$1.$2.$3/$4-$5');
  }
  return digitos.padStart(11, '0').replace(/^(\d{3})(\d{3})(\d{3})(\d{2})$/, '$1.$2.$3-$4');
};
